// GroupSettingsModal.tsx

import { StyledIconBase } from "@styled-icons/styled-icon";
import { Add, Delete, Pencil, Randomize, Remove, Reset } from "assets/icons/Common";
import { IconButtonInverted } from "components/primitives";
import { formatnameList } from "components/utils/formatNameList";
import { shuffle } from "components/utils/shuffle";
import { useConfirm } from "contexts/ConfirmContext";
import { ColorPicker } from "material-ui-color";
import React, { useState } from "react";
import { Modal, Button, Dropdown, DropdownButton } from "react-bootstrap";
import styled from "styled-components";

import { GroupConfigModel } from "./groupConfigPopUp";
import { GroupNamePopUp } from "./groupnamePopUp";
import { Group, SavedGroupConfigurations } from "./types";

// Styled Components
interface GroupSettingsModalProps {
  show: boolean;
  onClose: () => void;
  settings: any;
  savedGroupConfigurations: SavedGroupConfigurations[];
  currentStateGroupConfigData: SavedGroupConfigurations;
  setGroupConfig: React.Dispatch<React.SetStateAction<SavedGroupConfigurations>>;
  saveGroupConfigurationsGlobally: (newConfigurations: SavedGroupConfigurations[]) => void;
  selectedGroupConfigIndex: number | undefined;
  setSelectedGroupConfigIndex: (index: number | undefined) => void;
  setData: (value: [SavedGroupConfigurations]) => void;
  setSaveChangesAddButtonDisabled: (value: boolean) => void;
  isSaveChangedButtonDisabled: boolean;
  setIsAddButtonDisabled: (value: boolean) => void;
  isAddButtonDisabled: boolean;
  data: SavedGroupConfigurations[];
}

const GroupSettingsModal: React.FC<GroupSettingsModalProps> = ({
  show,
  onClose,
  settings,
  currentStateGroupConfigData,
  setGroupConfig,
  savedGroupConfigurations = [],
  saveGroupConfigurationsGlobally,
  selectedGroupConfigIndex,
  setSelectedGroupConfigIndex,
  setData,
  isSaveChangedButtonDisabled,
  setSaveChangesAddButtonDisabled,
  setIsAddButtonDisabled,
  isAddButtonDisabled,
  data,
}) => {
  //local states
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);
  const [isGroupNameEditorOpen, setGroupNameEditorOpen] = useState(false);
  const [isGroupConfigEditorOpen, setGroupConfigEditorOpen] = useState(false);

  const confirm = useConfirm();

  const handleSaveGroupColor = (selectedGroupIndex: number | null, newColor: string) => {
    if (selectedGroupIndex !== null) {
      setGroupConfig((prevConfig) => ({
        ...prevConfig,
        groups: prevConfig.groups.map((group, gIdx) =>
          gIdx === selectedGroupIndex
            ? { ...group, bgColor: newColor } // Update the group's title and bgColor
            : group,
        ),
      }));
    }
  };

  const handleGroupNameEditor = (groupIndex: number) => {
    setSelectedGroupIndex(groupIndex);
    setGroupNameEditorOpen(true);
  };
  const handleSaveGroupName = (selectedGroupIndex: number | null, newName: string) => {
    if (selectedGroupIndex !== null) {
      setGroupConfig((prevConfig) => ({
        ...prevConfig,
        groups: prevConfig.groups.map((group, gIdx) =>
          gIdx === selectedGroupIndex
            ? { ...group, title: newName } // Update the group's title
            : group,
        ),
      }));
    }
    setGroupNameEditorOpen(false); // Close the edit modal after saving
  };
  const handleRemoveStudent = (groupIndex: number, studentIndex: number) => {
    setGroupConfig((prevConfig) => {
      if (prevConfig.groups) {
        // Create a new object with the updated groups
        const updatedGroups = prevConfig.groups.map((group, gIdx) =>
          gIdx === groupIndex
            ? {
                ...group,
                students: group.students.filter((_, sIdx) => sIdx !== studentIndex),
              }
            : group,
        );

        // Return the updated config object with updated groups
        return {
          ...prevConfig,
          groups: updatedGroups,
        };
      }
      return prevConfig; // Return the same config if groups are not available
    });
  };
  const handleAdjustSlotCount = (adjustment: number) => {
    setGroupConfig((prevConfig) => {
      const updatedConfigData = { ...prevConfig };
      const { groups } = updatedConfigData;

      // Prevent adding more groups if already 20
      if (groups.length >= 20 && adjustment > 0) {
        setIsAddButtonDisabled(true);
        return updatedConfigData;
      }

      const existingGroupNumbers = groups.map((group) => {
        const match = group.title.match(/Group (\d+)/);
        return match ? parseInt(match[1], 10) : 0; // Use base 10 for parsing
      });
      const maxGroupNumber = existingGroupNumbers.length ? Math.max(...existingGroupNumbers) : 0;

      const newGroupCount = groups.length + adjustment;
      const newGroups = [...groups]; // Create a copy of the groups array

      // Add groups if under limit
      for (let i = groups.length; i < newGroupCount && newGroups.length < 20; i++) {
        const newGroup = {
          title: `Group ${maxGroupNumber + 1 + (i - groups.length)}`, // Ensure sequential numbering
          students: [],
          point: 0,
        };
        newGroups.push(newGroup);
      }

      updatedConfigData.groups = newGroups; // Replace the groups array
      updatedConfigData.groupCount = newGroups.length;

      // Update button states
      setIsAddButtonDisabled(newGroups.length >= 20);
      if (newGroups.length > 0) {
        setSaveChangesAddButtonDisabled(false);
      }

      return updatedConfigData;
    });
  };

  const handleSaveChanges = (configId: number | undefined) => {
    try {
      const updatedConfigurations = [...savedGroupConfigurations];

      const configIndex = updatedConfigurations.findIndex((config) => config.id === configId);
      if (configIndex !== -1) {
        updatedConfigurations[configIndex] = { ...currentStateGroupConfigData };
      } else {
        const newConfig = {
          ...currentStateGroupConfigData,
        };
        updatedConfigurations.push(newConfig);
      }
      saveGroupConfigurationsGlobally(updatedConfigurations);
      setSelectedGroupConfigIndex(configId);
      const selectedConfig = updatedConfigurations.find((config) => config.id === configId);
      if (selectedConfig) {
        setGroupConfig({
          ...selectedConfig,
        });
        setData([selectedConfig]);
      }
      onClose();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleEditGroupConfigName = (configId: number | undefined, newName: string) => {
    if (configId === currentStateGroupConfigData?.id) {
      setGroupConfig((prevConfig) => ({
        ...prevConfig,
        groupConfigName: newName,
      }));
    } else {
      setGroupConfig((prevConfig) => ({
        ...prevConfig,
        groupConfigName: newName,
      }));
    }
  };

  const handleDeleteGroup = (groupIndex: number) => {
    setGroupConfig((prevConfig) => {
      const updatedGroups = prevConfig.groups.filter((_, gIdx) => gIdx !== groupIndex);

      // Disable Save Changes button if no groups are left
      if (!updatedGroups?.length) {
        setSaveChangesAddButtonDisabled(true);
      } else {
        if (updatedGroups?.length < 20) {
          setIsAddButtonDisabled(false);
        }
        setSaveChangesAddButtonDisabled(false);
      }

      return {
        ...prevConfig,
        groups: updatedGroups,
      };
    });
  };

  const handleAddStudentToGroup = (groupIndex: number, studentName: string) => {
    setGroupConfig((prevConfig) => ({
      ...prevConfig,
      groups: prevConfig.groups.map((group, gIdx) =>
        gIdx === groupIndex ? { ...group, students: [...group.students, studentName] } : group,
      ),
    }));
  };

  const getUnassignedStudents = () => {
    const assignedStudents = getAllAssignedStudents();
    const allStudents = formatnameList(settings?.nameList);

    // Filter out the assigned students
    return allStudents.filter((student) => !assignedStudents.includes(student));
  };

  const getAllAssignedStudents = () => {
    let assignedStudents: string[] = [];
    currentStateGroupConfigData.groups.forEach((group) => {
      assignedStudents = [...assignedStudents, ...group.students];
    });
    return assignedStudents;
  };

  const getDisabledStudentsForGroup = (groupIndex: number) => {
    const assignedStudents = getAllAssignedStudents();
    const currentGroup = currentStateGroupConfigData.groups[groupIndex];
    return currentGroup ? [...assignedStudents, ...currentGroup.students] : assignedStudents;
  };

  const handleResetGroups = () => {
    setGroupConfig((prevConfig) => ({
      ...prevConfig,
      groups: prevConfig.groups.map((group) => ({
        ...group,
        students: [], // Clear students for each group
      })),
    }));
  };

  const handleConfirmReset = async () => {
    const confirmed = await confirm({
      title: "Info",
      message: "Are you sure you want to reset ? This will remove all students from all groups.",
      confirmText: "Ok",
      cancelText: "Cancel",
    });

    if (confirmed) {
      handleResetGroups();
    }

    // const confirmReset = window.confirm(
    //   "Are you sure you want to reset ? This will remove all students from all groups."
    // );
    // if (confirmReset) {
    //   handleResetGroups();
    // }
  };

  const handleIncrementPoint = (groupIndex: number) => {
    setGroupConfig((prevConfig) => ({
      ...prevConfig,
      groups: prevConfig.groups.map((group, gIdx) =>
        gIdx === groupIndex ? { ...group, point: group.point + 1 } : group,
      ),
    }));
  };

  const handleDecrementPoint = (groupIndex: number) => {
    setGroupConfig((prevConfig) => ({
      ...prevConfig,
      groups: prevConfig.groups.map((group, gIdx) =>
        gIdx === groupIndex && group.point > 0 ? { ...group, point: group.point - 1 } : group,
      ),
    }));
  };
  const randomiseGroups = () => {
    const cleanNameList: string[] = settings?.nameList?.split("\n").filter(Boolean) || [];
    const numberOfGroups = currentStateGroupConfigData?.groupCount || 1;
    const shuffledNames = shuffle(cleanNameList);

    // Reuse existing group titles, or default to "Group <index>" if no groups exist
    const existingGroups = currentStateGroupConfigData?.groups || [];
    const newGroups: Group[] = Array.from({ length: numberOfGroups }, (_, i) => ({
      bgColor: existingGroups[i]?.bgColor,
      title: existingGroups[i]?.title || `Group ${i + 1}`,
      students: [],
      point: existingGroups[i]?.point || 0,
    }));

    shuffledNames.forEach((name, index) => {
      newGroups[index % numberOfGroups].students.push(name);
    });

    setGroupConfig({
      ...currentStateGroupConfigData,
      groups: newGroups,
      groupCount: numberOfGroups,
      groupingMode: "Auto",
    });

    if (currentStateGroupConfigData?.groups?.length) {
      setSaveChangesAddButtonDisabled(false);
    }
  };

  const handleConfirmRandomize = () => {
    const confirmReset = window.confirm(
      "Are you sure you want to redistribute ? This will redistribute all students from all groups.",
    );
    if (confirmReset) {
      randomiseGroups();
    }
  };

  // On top layout
  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <ModalContent>
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Modal.Title>{currentStateGroupConfigData?.groupConfigName}</Modal.Title>
            <EditIcon
              withShadow={false}
              Icon={Pencil}
              onClick={() => setGroupConfigEditorOpen(true)}
              style={{ cursor: "pointer" }}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <ButtonControlsContainer>
            <IconButtonInverted
              Icon={Randomize}
              withShadow={false}
              onClick={() => handleConfirmRandomize()}
              // disabled={isAddButtonDisabled}
              title={"Randomize groups"}
            />
            <IconButtonInverted
              Icon={Add}
              withShadow={false}
              onClick={() => handleAdjustSlotCount(1)}
              disabled={isAddButtonDisabled}
              title={"Add group"}
            />
            <IconButtonInverted
              withShadow={false}
              Icon={Reset}
              accentColor="var(--danger)"
              title={"Remove groups"}
              onClick={() => handleConfirmReset()}
            />
            <Button
              variant="primary"
              className="save-changes-button" // Add a specific class
              style={{ color: "white", background: "#8f84de" }}
              onClick={() => handleSaveChanges(currentStateGroupConfigData?.id)}
              disabled={isSaveChangedButtonDisabled}
            >
              Save Changes
            </Button>
          </ButtonControlsContainer>
          {getUnassignedStudents()?.length > 0 && (
            <UnassignedStudentsContainer>
              {/* <h4>Unassigned Students</h4> */}

              <UnassignedStudentsRow>
                <GroupTitleContainer>
                  <GroupTitle>Unassigned Students</GroupTitle>
                </GroupTitleContainer>
                <UnassignedStudentsSlotElement>
                  {getUnassignedStudents().map((name, index) => (
                    <UnassignedStudentSlot key={index}>{name}</UnassignedStudentSlot>
                  ))}
                </UnassignedStudentsSlotElement>
              </UnassignedStudentsRow>
            </UnassignedStudentsContainer>
          )}
          <GroupsContainer>
            {currentStateGroupConfigData.groups.map((group, groupIndex) => (
              <GroupRow key={groupIndex}>
                <GroupTitleContainer>
                  <TitleWithEditContainer>
                    {/* Group Title */}
                    <GroupTitle>{group?.title}</GroupTitle>

                    {/* Edit Icon */}
                    <EditIcon
                      Icon={Pencil}
                      withShadow={false}
                      onClick={() => handleGroupNameEditor(groupIndex)}
                      style={{ cursor: "pointer" }}
                    />
                  </TitleWithEditContainer>

                  <ColorPaletteIcon>
                    <input
                      type="color"
                      value={group?.bgColor}
                      onChange={(e) => handleSaveGroupColor(groupIndex, e.target.value)}
                    />
                  </ColorPaletteIcon>

                  {settings?.organise?.[0]?.showPoints && (
                    <PointsContainer>
                      <span>Points </span>
                      <IconButtonInverted
                        withShadow={false}
                        Icon={Remove}
                        onClick={() => handleDecrementPoint(groupIndex)}
                        style={{
                          marginRight: "10px",
                          width: "20px",
                          height: "20px",
                          marginLeft: "13px",
                        }}
                      />
                      <span>{group.point}</span>

                      <IconButtonInverted
                        Icon={Add}
                        withShadow={false}
                        onClick={() => handleIncrementPoint(groupIndex)}
                        style={{
                          marginLeft: "10px",
                          width: "20px",
                          height: "20px",
                        }}
                      />
                    </PointsContainer>
                  )}
                  <DropdownButton title="Assign Student" style={{ marginLeft: "8px" }}>
                    <div
                      style={{
                        maxHeight: "220px",
                        overflowY: "auto",
                      }}
                    >
                      {/* Display unassigned students only */}
                      {getUnassignedStudents().map((name: string, index: number) => {
                        const disabled = getDisabledStudentsForGroup(groupIndex).includes(name);
                        return (
                          <Dropdown.Item
                            key={index}
                            eventKey={name}
                            onClick={() => !disabled && handleAddStudentToGroup(groupIndex, name)}
                            disabled={disabled}
                          >
                            {name}
                          </Dropdown.Item>
                        );
                      })}
                    </div>
                  </DropdownButton>

                  <DeleteIcon // Add delete icon beside the edit icon
                    Icon={Delete}
                    onClick={() => handleDeleteGroup(groupIndex)}
                    style={{
                      cursor: "pointer",
                      background: "#ff9090",
                      boxShadow: "none",
                    }}
                  />
                </GroupTitleContainer>
                <GroupSlotElements>
                  {group.students.map((student, studentIndex) => (
                    <GroupSlot key={studentIndex}>
                      <span>{student}</span>
                      <SlotClearButton
                        Icon={Remove}
                        style={{
                          cursor: "pointer",
                          boxShadow: "none",
                        }}
                        onClick={() => handleRemoveStudent(groupIndex, studentIndex)}
                      />
                    </GroupSlot>
                  ))}
                </GroupSlotElements>
              </GroupRow>
            ))}
          </GroupsContainer>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </ModalContent>
      {isGroupNameEditorOpen && selectedGroupIndex !== null && (
        <GroupNamePopUp
          isOpen={isGroupNameEditorOpen}
          onClose={() => setGroupNameEditorOpen(false)}
          words={currentStateGroupConfigData?.groups[selectedGroupIndex]?.title}
          onSave={(newName: string) => handleSaveGroupName(selectedGroupIndex, newName)}
        />
      )}
      {isGroupConfigEditorOpen && (
        <GroupConfigModel
          isOpen={isGroupConfigEditorOpen}
          onClose={() => setGroupConfigEditorOpen(false)}
          words={currentStateGroupConfigData?.groupConfigName || "Enter Configuration Name"}
          onSave={(newName: string) => handleEditGroupConfigName(selectedGroupConfigIndex, newName)}
        />
      )}
    </Modal>
  );
};

export default GroupSettingsModal;

// Styling
const ColorPaletteIcon = styled.label`
  margin-bottom: 0;
  input {
    width: 50px;
    height: 46px;
    border-radius: 8px;
    border: 2px solid rgb(255, 143, 113);
    background-color: #fff;
  }
`;

const ModalContent = styled.div`
  max-height: 80vh;
  overflow-y: auto;
`;

const PointsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 16px;
`;

const ButtonControlsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  height: 100%;
  gap: 0.5cqw;
  padding: 1cqw;

  button {
    width: 2cqw;
    height: 2cqw;
    padding: 5px;
  }
  .save-changes-button {
    width: auto; /* Set auto width to make it expand */
    min-width: 6cqw; /* Optional: set a minimum width to make it a bit larger */
  }
`;

const GroupsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const UnassignedStudentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const UnassignedStudentsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #f8f9fa;
  margin-bottom: 16px;
`;

const UnassignedStudentsSlotElement = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  flex: 3;
`;

const UnassignedStudentSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  gap: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  position: relative;
  background-color: #f8f9fa;
  font-size: 12px;
`;

const GroupRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #f8f9fa;
  margin-bottom: 16px;
`;

const GroupTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const TitleWithEditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1px; /* Reduced space between the title and edit icon */
  flex: 1;
`;

const GroupTitle = styled.span`
  font-size: 14px;
  font-weight: bold;
  padding: 5px;
  width: auto !important; /* Prevents stretching */
  border: none;
  background: transparent;
  &:focus {
    outline: none;
  }
`;

const EditIcon = styled(IconButtonInverted)`
  width: 30px;
  height: 30px;
  cursor: pointer;
  svg {
    max-width: 14px; /* Reduced icon size */
    path {
      fill: black;
    }
  }
`;
const DeleteIcon = styled(IconButtonInverted)`
  width: 30px;
  height: 30px;
  margin-left: 8px;
  cursor: pointer;
`;

const GroupSlotElements = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  flex: 3;
`;

const GroupSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  gap: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  position: relative;
  background-color: #f8f9fa;
  font-size: 12px;
`;

const SlotClearButton = styled(IconButtonInverted)`
  position: relative;
  margin-left: 10px;
  ${StyledIconBase} {
    width: 12px;
    height: 12px;
  }
`;

// const UnassignedStudentsContainer = styled.div`
//   margin-bottom: 20px;
// `;

// const UnassignedStudentsRow = styled.div`
//   display: flex;
//   gap: 10px;
//   flex-wrap: wrap;
// `;

const UnassignedStudent = styled.span`
  background-color: #f1f1f1;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background-color: #ddd;
  }
`;
